import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import InitialsAvatar from "@/components/base/InitialsAvatar";
import WorkCard from "@/components/base/WorkCard";
import { getRelatedWorks, getReviewsByWork, getWorkById } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const work = id ? getWorkById(id) : undefined;
  const [copied, setCopied] = useState(false);

  usePageMeta(
    work ? `${work.title}（${work.year}）详情 - 疯狂的欲望` : "影视详情 - 疯狂的欲望",
    work
      ? `${work.title}（${work.year}）剧情介绍、演员阵容与影评分析。${work.synopsis.slice(0, 60)}`
      : undefined,
  );

  if (!work) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-[1400px] flex-col items-center justify-center px-4 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这部作品暂时没有资料</h1>
        <p className="mt-2 text-sm text-foreground-600">可能链接已失效，去影视列表看看其他推荐吧。</p>
        <Link to="/category/movie" className="mt-6 cursor-pointer rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
          返回影视列表
        </Link>
      </div>
    );
  }

  const reviews = getReviewsByWork(work.id).slice(0, 3);
  const related = getRelatedWorks(work, 4);
  const infoRows = [
    { label: "类型", value: work.genres.join(" / ") },
    { label: "地区", value: work.region },
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.map((c) => c.name).join("、") },
    { label: work.type === "movie" ? "上映时间" : "播出时间", value: work.releaseDate },
    { label: work.type === "movie" ? "片长" : "集数", value: work.duration },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={work.heroImage}
          alt={`${work.title} 宣传图`}
          title={`${work.title} 影视详情`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/50" />

        <div className="relative mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-background-300">
            <Link to="/" className="cursor-pointer hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${work.type}`} className="cursor-pointer hover:text-background-50">
              {work.type === "movie" ? "电影" : "电视剧"}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">{work.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-36 shrink-0 overflow-hidden rounded-lg md:w-56">
              <img
                src={work.cover}
                alt={`${work.title} 海报`}
                title={`${work.title} 海报`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>

            <div className="flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
                  {work.type === "movie" ? "电影" : "电视剧"}
                </span>
                {work.tags.map((t) => (
                  <span key={t} className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-100">
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-3xl font-black text-background-50 md:text-5xl">{work.title}</h1>
              <p className="mt-3 font-heading text-base italic text-accent-300 md:text-lg">「{work.tagline}」</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                <span className="flex items-center gap-1.5 font-heading text-3xl font-black text-accent-400">
                  <i className="ri-star-fill text-2xl" />
                  {work.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-background-200">
                  <i className="ri-fire-line text-accent-400" />
                  热度 {(work.heat / 1000).toFixed(1)}k
                </span>
                <span className="text-sm text-background-200">{work.year} · {work.region}</span>
                <span className="text-sm text-background-200">{work.duration}</span>
              </div>

              <p className="mt-5 max-w-2xl line-clamp-3 text-sm leading-relaxed text-background-200">{work.synopsis}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="#synopsis"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  查看完整剧情
                </a>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className={copied ? "ri-check-line" : "ri-link"} />
                  {copied ? "已复制链接" : "复制链接"}
                </button>
                <span className="flex items-center gap-2">
                  {[
                    { icon: "ri-weibo-line", label: "分享到微博" },
                    { icon: "ri-wechat-line", label: "分享到微信" },
                    { icon: "ri-share-forward-line", label: "更多分享" },
                  ].map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      aria-label={s.label}
                      title={s.label}
                      className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
                    >
                      <i className={`${s.icon} text-lg`} />
                    </button>
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
        <div className="flex flex-col gap-10 lg:flex-row">
          <div className="min-w-0 flex-1 space-y-12">
            <section id="synopsis">
              <SectionTitle icon="ri-quill-pen-line" title="剧情简介" />
              <p className="mt-4 whitespace-pre-line text-sm leading-loose text-foreground-700 md:text-base">
                {work.synopsis}
              </p>
            </section>

            <section>
              <SectionTitle icon="ri-group-line" title="演员阵容" />
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                {work.cast.map((member) => (
                  <div
                    key={member.name}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <InitialsAvatar name={member.name} size={52} />
                    <div className="min-w-0">
                      <h4 className="font-heading text-base font-bold text-foreground-950">{member.name}</h4>
                      <p className="text-xs font-medium text-accent-700">{member.role}</p>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <SectionTitle icon="ri-book-open-line" title="影评精选" />
              {reviews.length > 0 ? (
                <div className="mt-4 space-y-4">
                  {reviews.map((review) => (
                    <Link
                      key={review.id}
                      to={`/review/topic/${review.topicId}`}
                      className="group block cursor-pointer rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
                    >
                      <h4 className="font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700">
                        {review.title}
                      </h4>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{review.excerpt}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span className="font-medium text-accent-700">{review.author}</span>
                        <span>· {review.date}</span>
                        <span className="ml-auto flex items-center gap-1 text-primary-700">
                          阅读全文 <i className="ri-arrow-right-line" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 p-6 text-sm text-foreground-600">
                  这部作品暂无影评，欢迎关注后续更新。
                </p>
              )}
            </section>
          </div>

          <aside className="w-full shrink-0 lg:w-80">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">详细信息</h3>
                <dl className="mt-4 space-y-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-sm">
                      <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 flex-1 text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-lg bg-secondary-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                  <i className="ri-fire-line text-accent-600" />
                  热门标签
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[...work.genres, ...work.tags].map((t) => (
                    <span key={t} className="rounded-full bg-background-50 px-3 py-1 text-xs text-secondary-900">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <SectionTitle icon="ri-movie-2-line" title="相关推荐" />
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
              {related.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
        <i className={icon} />
      </span>
      {title}
    </h2>
  );
}